import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import React from "react";
import { styles } from "..";
import moment from "moment";

const tableStyle = StyleSheet.create({
  card: {
    border: "1px solid #888",
    padding: "8px",
    borderRadius: "4px",
    marginBottom: 10,
  },
  headerLabel: {
    fontSize: 16,
    fontWeight: "bold",
    marginRight: 16,
  },
  valueLabel: {
    fontSize: 16,
    fontWeight: 400,
    marginRight: 16,
  },
  table: {
    width: "100%",
    marginTop: 0,
    borderWidth: 1,
    borderColor: "#888",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#888",
  },
  mainHeader: {
    width: "70%",
    padding: 5,
    backgroundColor: "#f0f0f0",
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "left",
  },
  subHeader: {
    width: "10%",
    padding: 5,
    backgroundColor: "#f0f0f0",
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
  },
  mainCell: {
    width: "80%",
    padding: 5,
    fontSize: 10,
    textAlign: "left",
  },
  subCell: {
    width: "10%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
  },
  nestedHeader: {
    width: "80%",
    padding: 5,
    fontSize: 10,
    backgroundColor: "#f8f9fa",
  },
  nestedSubHeader: {
    width: "10%",
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    backgroundColor: "#f8f9fa",
  },
  benefitRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#ccc",
  },
  spacerRow: {
    border: "1px solid #ccc",
    padding: "8px",
  },
  successText: {
    color: "#28a745",
    fontWeight: "bold",
    fontSize: 10,
  },
  mutedText: {
    color: "#6c757d",
    fontSize: 9,
  },
  warningBg: {
    backgroundColor: "#fff3cd",
  },
  successBg: {
    backgroundColor: "#d1e7dd",
  },
});

const formatValue = (benefit) => {
  if (benefit.allowedMoney) {
    return `${benefit.allowedMoney.value} ${benefit.allowedMoney.currency}`;
  }
  if (benefit.allowedUnsignedInt !== undefined) {
    return `${benefit.allowedUnsignedInt}%`;
  }
  return "";
};

const formatUsedValue = (benefit) => {
  if (benefit.usedMoney) {
    return `${benefit.usedMoney.value} ${benefit.usedMoney.currency}`;
  }
  return "";
};

const BenefitsTable = ({ printData, printedName }) => {
  const patinetInfo = printData?.patient;

  const items = printData?.benefits || [];
  const coveragePersonal = printData?.coverage_info;
  const payerPersonal = printData?.payer_response;
  return (
    <Document>
      <Page size="A3" style={styles.page}>
        {/* Patient Information Section */}
        <View style={tableStyle.card}>
          <View style={styles.flexRow}>
            <Text style={styles.dottedLine}>
              <Text style={styles.s1textLabel}>Patient Name:{"  "}</Text>
              <Text style={styles.s1textvalue}>
                {`${patinetInfo?.patient_firstname || ""} ${
                  patinetInfo?.patient_familyname
                }`}
              </Text>
            </Text>
            <Text style={styles.dottedLine}>
              <Text style={styles.s1textLabel}>
                National ID / Iqama No.{"  "}
              </Text>
              <Text style={styles.s1textvalue}>
                {patinetInfo?.patient_nationalid || ""}
              </Text>
            </Text>
            <Text style={styles.dottedLine}>
              <Text style={styles.s1textLabel}>Policy Holder Name:{"  "}</Text>
              <Text style={styles.s1textvalue}>
                {patinetInfo?.sponsor_name || ""}
              </Text>
            </Text>
          </View>
          <View style={styles.flexRow}>
            <Text style={styles.dottedLine}>
              <Text style={styles.s1textLabel}>Payer Name:{"  "}</Text>
              <Text style={styles.s1textvalue}>
                {payerPersonal?.payer_name || ""}
              </Text>
            </Text>
            <Text style={styles.dottedLine}>
              <Text style={styles.s1textLabel}>MemberShip No.{"  "}</Text>
              <Text style={styles.s1textvalue}>
                {coveragePersonal?.memberid}
              </Text>
            </Text>
            <Text style={styles.dottedLine}>
              <Text style={styles.s1textLabel}>Policy No:{"  "}</Text>
              <Text style={styles.s1textvalue}>
                {coveragePersonal?.policyno}
              </Text>
            </Text>
          </View>
          <View style={styles.flexRow}>
            <Text style={styles.dottedLine}>
              <Text style={styles.s1textLabel}>Class:{"  "}</Text>
              <Text style={styles.s1textvalue}>
                {coveragePersonal?.class || ""}
              </Text>
            </Text>
            <Text style={styles.dottedLine}>
              <Text style={styles.s1textLabel}>Expiry Date.{"  "}</Text>
              <Text style={styles.s1textvalue}>{payerPersonal?.serviceto}</Text>
            </Text>
            <Text style={styles.dottedLine}>
              <Text style={styles.s1textLabel}>Inception Date:{"  "}</Text>
              <Text style={styles.s1textvalue}>
                {payerPersonal?.servicefrom}
              </Text>
            </Text>
          </View>
        </View>

        {/* Benefits Table Section */}
        {items?.length > 0 && (
          <View style={tableStyle.table}>
            {/* Table Header */}
            <View style={tableStyle.tableRow}>
              <Text style={tableStyle.mainHeader}>Benefits Details</Text>
              <Text style={tableStyle.subHeader}>Network</Text>
              <Text style={tableStyle.subHeader}>Unit</Text>
              <Text style={tableStyle.subHeader}>Term</Text>
            </View>

            {/* Table Body */}
            {items.map((item, index) => (
              <View key={index}>
                {/* Main Benefit Row */}
                <View style={tableStyle.tableRow}>
                  <View style={tableStyle.mainCell}>
                    <Text style={tableStyle.successText}>
                      {item?.category?.coding?.[0]?.display || item?.name}
                    </Text>
                    <Text style={tableStyle.mutedText}>
                      {item?.description}
                    </Text>
                  </View>
                  <Text style={tableStyle.subCell}>
                    {item?.network?.coding?.[0]?.code || ""} Network
                  </Text>
                  <Text style={tableStyle.subCell}>
                    {item?.unit?.coding?.[0]?.code || ""}
                  </Text>
                  <Text style={tableStyle.subCell}>
                    {item?.term?.coding?.[0]?.code || ""}
                  </Text>
                </View>

                {/* Nested Benefits Header */}
                <View
                  style={[tableStyle.tableRow, { backgroundColor: "#f8f9fa" }]}
                >
                  <Text style={tableStyle.nestedHeader}></Text>
                  <Text style={tableStyle.nestedSubHeader}>Allowed</Text>
                  <Text style={tableStyle.nestedSubHeader}>Used</Text>
                </View>

                {/* Nested Benefits Rows */}
                {item?.benefit?.map((benefit, benefitIndex) => (
                  <View key={benefitIndex} style={tableStyle.benefitRow}>
                    <Text style={[tableStyle.mainCell, { paddingLeft: 15 }]}>
                      {benefit?.type?.coding?.[0]?.code.replace(/-/g, " ") ||
                        "Benefit"}
                    </Text>
                    <Text style={[tableStyle.subCell, tableStyle.warningBg]}>
                      {formatValue(benefit)}
                    </Text>
                    <Text style={[tableStyle.subCell, tableStyle.successBg]}>
                      {formatUsedValue(benefit)}
                    </Text>
                  </View>
                ))}

                <View style={tableStyle.spacerRow} />
              </View>
            ))}
          </View>
        )}

        {/* Empty State */}
        {items.length === 0 && (
          <Text style={{ marginTop: 10, textAlign: "center" }}>
            No benefits data available
          </Text>
        )}

        <Text style={styles.createdBy} fixed>
          {`Printed by: | ${printedName || ""} ${moment().format("lll")}`}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber || ""} of ${totalPages || ""}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default BenefitsTable;
